import * as React from "react";
import { Thread } from "@/components/assistant-ui/thread";
import { MyRuntimeProviderAlphaCreate } from "@/alphaPopup/components/MyRuntimeProvider";
import { Button, Popover } from "antd";
import { useAlphaAgentStore } from "@/state/alphaAgentStore";
import { StepIndicator } from "@/components/ui/StepIndicator";
import FormatAndPlaceholder from "@/alphaPopup/components/FormatAndPlaceholder";
import { MyRuntimeProviderFeatureDetails } from "@/alphaPopup/components/MyRuntimeProviderFeatureDetails";
import CustomFeatures from "@/alphaPopup/components/CustomFeatures";
import clsx from "clsx";
import { getSelectedShape, removeSelectedShape } from "@/utils/sharePricePerformance/selectedPlaceHolderLocalStorage";
import { UICheckbox } from "@alphascriptsolutions/client-core";
import { CSSTransition } from "react-transition-group";
import { labels } from "@/utils/alphaAgent/helper";
import { resetLLM } from "@/utils/alphaAgent/resetLLM";
import { addFieldsToData, transformGlobalState } from "@/utils/alphaAgent/modelDataHelper";
import { getToken } from "@/utils/localstorage";
import axios from "axios";
import UIFormatTemplateSelection from "@alphascriptsolutions/client-core/dist/uiFormatTemplateSelection";

function Step({ show, children }) {
  const nodeRef = React.useRef<HTMLDivElement | null>(null);
  return (
    <CSSTransition
      in={show} // ← visible or hidden?
      timeout={150}
      classNames="step" // ← base for all generated class-names
      mountOnEnter={false} // keep it in the DOM from the first render
      unmountOnExit={false} // never un-mount (so state survives)
      appear={show}
      nodeRef={nodeRef}
    >
      <div ref={nodeRef} className="step-slot">{children}</div>
    </CSSTransition>
  );
}

const AlphaPopup: React.FC = () => {
  const {
    currentStep,
    questionsPerStep,
    setCurrentStep,
    setDisabledButton,
    setIsChatCompleted,
    isChatCompleted,
    disableBackButton,
  } = useAlphaAgentStore();
  const disabledButton = useAlphaAgentStore((state) => state.disabledButton);
  const answersByStep = useAlphaAgentStore((state) => state.answersByStep);
  const getMessage = () => {
    if (currentStep === 1) {
      return "Going back to step 1 will remove all entries completed on the Feature Details page.";
    } else if (currentStep === 2) {
      return "Going back to Step 2 will clear only the current feature’s configuration.";
    }
    return "";
  };
  const handleNextClick = React.useCallback(async () => {
    if (currentStep === 3) {
      const rawData = localStorage.getItem("alphaCreateData");
      const globalStateData = JSON.parse(rawData);
      const convertedDataToRequiredForm = transformGlobalState(globalStateData);
      const { model } = useAlphaAgentStore.getState();

      const presentationDesign = model.getByName<UIFormatTemplateSelection>("presentationDesign");
      const formatTemplateSource = model.getByName<UICheckbox>("formatTemplateSource");
      const formatTemplateNotes = model.getByName<UICheckbox>("formatTemplateNotes");
      const selectedPlaceholder = getSelectedShape();
      const completeData = addFieldsToData({
        chartObject: convertedDataToRequiredForm,
        customFormatId: presentationDesign.value,
        customPlaceholder: selectedPlaceholder,
        metadata: {
          source: formatTemplateSource?.value?.length > 0,
          notes: formatTemplateNotes?.value?.length > 0,
        },
        tradingVolume: localStorage.getItem("tradingVolume") === "true",
      });
      localStorage.setItem("ModelDataToBackend", JSON.stringify(completeData));
      localStorage.setItem("modelGlobalState", rawData);
      removeSelectedShape();
      localStorage.removeItem("ModelData");
      localStorage.removeItem("PanelsData");
      localStorage.removeItem("currencySelectionSlotValue");
      localStorage.removeItem("AlphaAgentPlaceholder");
      localStorage.removeItem("alphaCreateData");
      localStorage.removeItem("messages");
      Office.context.ui.messageParent("INSERT_CHART");
      await resetLLM();
      setIsChatCompleted(true);
      return;
    }
    setCurrentStep(currentStep + 1);
    const { setIsEditInitial } = useAlphaAgentStore.getState();
    setIsEditInitial(false);
    setDisabledButton(true);
  }, [currentStep, setCurrentStep, setDisabledButton]);

  if (isChatCompleted) {
    return (
      <div
        style={{
          boxSizing: "border-box",
          padding: "0 50px",
          fontWeight: "bold",
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          textAlign: "center",
          gap: "10px",
        }}
      >
        <p className="text-gray-800">
          Thank you. We are working on the Excel model and placeholder content you requested. We estimate this to take 1
          – 2 minutes. Your Taskpane will open when the content is ready.
        </p>
        <Button type="primary" size="large" onClick={() => Office.context.ui.messageParent("CLOSE")}>
          Close
        </Button>
      </div>
    );
  }

  const handleCancelButton = async () => {
    await resetLLM();
    localStorage.removeItem("messages");
    localStorage.removeItem("ModelData");
    localStorage.removeItem("alphaCreateData");
    localStorage.removeItem("PanelsData");
    localStorage.removeItem("currencySelectionSlotValue");
    localStorage.removeItem("AlphaAgentPlaceholder");
    Office.context.ui.messageParent("CLOSE");
  };
  // @ts-ignore
  // @ts-ignore
  return (
    <div
      className="flex flex-col justify-between h-full min-h-screen grid-rows-[auto_1fr_auto] gap-x-2 px-4 py-4"
      // style={{
      //   ["--thread-max-width" as string]: "45rem",
      // }}
    >
      <img src="assets/AlphaScript_Logo_Dark_cropped.png" alt="" className="w-36 h-auto object-contain mb-2" />
      <div
        className={clsx(
          "w-full mx-auto max-w-[var(--thread-max-width)] py-4 shadow-md px-4 rounded-sm z-10 border border-gray-200 border-b-[2px] border-b-[#ff9336]"
          // currentStep === 1 && "fixed top-0 left-0 right-0 bg-white z-20"
        )}
      >
        <StepIndicator
          currentStep={currentStep}
          answersByStep={answersByStep}
          questionsPerStep={questionsPerStep}
          labels={labels}
        />
      </div>

      <div className={"grid h-full relative w-full"}>
        <Step show={currentStep === 0}>
          <MyRuntimeProviderAlphaCreate>
            <Thread />
          </MyRuntimeProviderAlphaCreate>
        </Step>

        <Step show={currentStep === 1}>
          <CustomFeatures />
        </Step>

        <Step show={currentStep === 2}>
          <MyRuntimeProviderFeatureDetails>
            <Thread />
          </MyRuntimeProviderFeatureDetails>
        </Step>

        <Step show={currentStep === 3}>
          <FormatAndPlaceholder />
        </Step>
      </div>
      <div className="flex justify-between w-full px-2 pt-4 mx-auto max-w-[var(--thread-max-width)]">
        <Button danger onClick={handleCancelButton}>
          Cancel
        </Button>

        <div className="flex gap-2">
          {currentStep !== 0 && currentStep !== 3 && (
            <Popover
              content={
                <div style={{ width: "250px" }}>
                  <p>{getMessage()}</p>
                </div>
              }
              title="Are you sure?"
              trigger="hover"
            >
              <Button
                type="default"
                disabled={disableBackButton}
                onClick={async () => {
                  const { currentStep, setCurrentStep, setIsEditInitial, updateAnswerByStep } =
                    useAlphaAgentStore.getState();
                  if (currentStep > 0) setCurrentStep(currentStep - 1);
                  if (currentStep === 1) {
                    setIsEditInitial(true);
                    updateAnswerByStep(0, 3);
                  }
                  const apiUrl = process.env.API_URL;
                  const authToken = await getToken();
                  await axios.post(
                    `${apiUrl}/alpha-create/interact`,
                    { jumpToStep: currentStep },
                    {
                      headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${authToken}`,
                      },
                    }
                  );
                }}
              >
                Back
              </Button>
            </Popover>
          )}

          <Button
            type={"primary"}
            disabled={disabledButton && answersByStep[currentStep] !== questionsPerStep[currentStep]}
            className={""}
            onClick={handleNextClick}
          >
            {currentStep === 3 ? "Complete" : "Next"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AlphaPopup;
